import { Tooltip, TooltipContent, TooltipTrigger } from '@lms/ui/tooltip';
import { cn } from '@lms/ui/utils';
import type * as React from 'react';
import { formatNumber, formatPercent } from '../format';

export interface ShareRow {
  id: string | number;
  name: string;
  /** How many of `total` meet the condition (e.g. groups with completions). */
  value: number;
  total: number;
  /**
   * Optional split of `value`/`total` (e.g. tenants and suppliers): one
   * percentage per part and a stacked meter, with each part's
   * "value / total label" in a tooltip. Parts keep their position (and
   * shade) across rows; one with a zero total shows "–".
   */
  parts?: readonly ShareRowPart[];
}

export interface ShareRowPart {
  id: string;
  label: string;
  value: number;
  total: number;
}

// Segment shades for split rows, in part order.
const PART_CLASSES = ['bg-primary', 'bg-primary/50'];

/**
 * One row per item with "value of total" and a meter: a compact way to
 * compare shares across many items. Rows are buttons when `onSelect` is
 * given, so they can drive a filter elsewhere on the page.
 */
export function ShareRows({
  rows,
  describe,
  selected,
  onSelect,
  locale,
}: {
  rows: readonly ShareRow[];
  /**
   * Text beside the name, e.g. "212 of 270 groups have completions". Not
   * used for rows with `parts`.
   */
  describe?: (row: ShareRow) => React.ReactNode;
  selected?: ShareRow['id'];
  onSelect?: (id: ShareRow['id']) => void;
  locale?: string;
}) {
  const legend = rows.find((r) => r.parts && r.parts.length > 1)?.parts;
  return (
    <div className="flex flex-col gap-2">
      {legend && (
        <ul className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {legend.map((part, i) => (
            <li key={part.id} className="flex items-center gap-1.5">
              <span
                aria-hidden
                className={cn('size-2 rounded-full', PART_CLASSES[i])}
              />
              {part.label}
            </li>
          ))}
        </ul>
      )}
      <ul className="-mx-2 flex flex-col">
        {rows.map((row) => {
          const ratio = row.total > 0 ? row.value / row.total : 0;
          const isSelected = selected === row.id;
          const parts = row.parts;
          const content = (
            <>
              <div className="flex items-baseline gap-3">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {row.name}
                </span>
                {parts ? (
                  <span className="flex shrink-0 items-center gap-5 text-sm font-medium tabular-nums">
                    {parts.map((p, i) => (
                      <span key={p.id} className="flex items-center gap-1.5">
                        <span
                          aria-hidden
                          className={cn('size-2 rounded-full', PART_CLASSES[i])}
                        />
                        <span className="w-10 text-right">
                          {p.total > 0
                            ? formatPercent(p.value / p.total, locale)
                            : '–'}
                        </span>
                      </span>
                    ))}
                  </span>
                ) : (
                  <>
                    <span className="text-muted-foreground shrink-0 text-xs">
                      {describe?.(row)}
                    </span>
                    <span className="w-10 shrink-0 text-right text-sm font-medium tabular-nums">
                      {formatPercent(ratio, locale)}
                    </span>
                  </>
                )}
              </div>
              <div
                aria-hidden
                className="bg-primary/15 mt-2 flex h-1.5 overflow-hidden rounded-full"
              >
                {parts ? (
                  parts.map((p, i) => (
                    <div
                      key={p.id}
                      className={cn('h-full', PART_CLASSES[i])}
                      style={{
                        width: `${row.total > 0 ? (p.value / row.total) * 100 : 0}%`,
                      }}
                    />
                  ))
                ) : (
                  <div
                    className="bg-primary h-full rounded-full"
                    style={{ width: `${ratio * 100}%` }}
                  />
                )}
              </div>
            </>
          );
          const item = onSelect ? (
            <button
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelect(row.id)}
              className={cn(
                'hover:bg-muted/60 focus-visible:ring-ring w-full rounded-md px-2 py-2.5 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none',
                isSelected && 'bg-muted',
              )}
            >
              {content}
            </button>
          ) : (
            <div className="px-2 py-2.5">{content}</div>
          );
          return (
            <li key={row.id}>
              {parts ? (
                <Tooltip>
                  <TooltipTrigger asChild>{item}</TooltipTrigger>
                  <TooltipContent>
                    <ul className="flex flex-col gap-1">
                      {parts.map((p, i) =>
                        p.total > 0 ? (
                          <li key={p.id} className="flex items-center gap-1.5">
                            <span
                              aria-hidden
                              className={cn(
                                'size-2 rounded-full',
                                PART_CLASSES[i],
                              )}
                            />
                            <span className="tabular-nums">
                              {formatNumber(p.value, locale)} /{' '}
                              {formatNumber(p.total, locale)}
                            </span>
                            {p.label.toLocaleLowerCase(locale)}
                          </li>
                        ) : null,
                      )}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              ) : (
                item
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
